/**
 * The second visual channel for FILLED shapes — the donut's arcs and the
 * funnel's rungs.
 *
 * `chartPalette` gives every stage a (hue, tier) pair, because eight hues
 * cannot separate thirteen stages and the alternative was to let a stage change
 * colour between tabs. A LINE renders its tier as a `stroke-dasharray` and that
 * is the end of it. A filled arc has no stroke to dash, so it renders the same
 * tier as a HATCH: diagonal stripes in the surface colour cut across the hue,
 * at the density the matching dash pattern has. `Carve Out` is a hatched
 * `Source` on the donut and a dashed `Source` on the progress chart — one idea,
 * two renderings, because SVG offers no third option.
 *
 * WHY THE STRIPES ARE `stroke-surface` AND NOT WHITE OR BLACK. The stripes are
 * meant to read as the background showing through, which is what makes the
 * result "the same hue, interrupted" rather than "a second, streaky colour".
 * The token flips with the theme, so the hatch survives dark and light the way
 * the dash does.
 *
 * IDS ARE SCOPED PER COMPONENT INSTANCE. An SVG `<pattern>` is referenced by
 * document-wide id, and the dashboard renders a donut, a funnel and — in the
 * downloaded report — a second copy of both. A fixed id would have every one of
 * them resolve to whichever `<defs>` the document happened to hold first, and
 * the sanitiser matters as much as the `useId`: React 19 hands back ids
 * containing characters that are legal in an `id` attribute and awkward inside
 * a `url(#...)` reference.
 *
 * Geometry rides in ATTRIBUTES and colour in CLASSES, so the no-inline-styles
 * rule holds here as it does in the charts themselves.
 */

import { useId } from "react";

/**
 * Stripe geometry per tier, in user units — index 0 is tier 1.
 *
 * The densities mirror `SERIES_DASH`: tier 1 is the widely-spaced dash, tier 2
 * the fine dotted one, tier 3 the long dash-dot. A reader who learns "sparse
 * stripes = Carve Out" on the donut meets the same sparseness as a dash on the
 * progress chart.
 */
const HATCH_TIERS = [
  { gap: 6, width: 2 },
  { gap: 3, width: 1 },
  { gap: 8, width: 3 },
] as const;

/**
 * A `<defs>` id prefix unique to the calling component instance.
 *
 * A hook rather than a counter so it is stable across re-renders and correct
 * under `renderToStaticMarkup`, which the downloaded report uses.
 */
export function useHatchScope(): string {
  return `h${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
}

/**
 * The `fill` attribute for a shape at `tier`, or `undefined` for tier 0.
 *
 * `undefined` rather than `"none"`: the shape's own `fill-chart-*` class paints
 * the hue, and this overlay only exists to interrupt it.
 */
export function hatchFill(scope: string, tier: number): string | undefined {
  return tier > 0 && tier <= HATCH_TIERS.length ? `url(#${scope}-hatch-${tier})` : undefined;
}

export interface StageHatchDefsProps {
  /** From {@link useHatchScope}, in the component that owns the `<svg>`. */
  scope: string;
  /**
   * Multiplies the stripe geometry, for a swatch drawn in a 10-unit box rather
   * than a 220-unit chart. One hatch at two sizes rather than two hatches, so
   * the key cannot drift from the arc it keys.
   */
  scale?: number;
}

/** The three hatch patterns, emitted once per `<svg>` that uses them. */
export function StageHatchDefs({ scope, scale = 1 }: StageHatchDefsProps) {
  return (
    <defs>
      {HATCH_TIERS.map(({ gap, width }, i) => {
        const id = `${scope}-hatch-${i + 1}`;
        return (
          <pattern
            key={id}
            id={id}
            width={gap * scale}
            height={gap * scale}
            patternUnits="userSpaceOnUse"
            patternTransform="rotate(45)"
          >
            <line
              x1={0}
              y1={0}
              x2={0}
              y2={gap * scale}
              strokeWidth={width * scale}
              className="stroke-surface"
            />
          </pattern>
        );
      })}
    </defs>
  );
}

export interface StageSwatchDotProps {
  /** The stage's `bg-chart-*` class. */
  bgClass: string;
  /** The stage's texture tier; 0 leaves the dot flat. */
  tier: number;
}

/**
 * The legend dot for one stage, hatched when the stage's tier says so.
 *
 * STILL A DOT. Jared, 2026-08-25, on the funnel key: *"color dot word number
 * percent just like this here also"*. Two stages that share a hue would have
 * given him two identical dots, which is a key that keys nothing — so the dot
 * gained a texture rather than becoming a line. The tooltip beside it uses
 * `SeriesSwatch`, a dashed line, and the two agree because both read the same
 * tier.
 */
export function StageSwatchDot({ bgClass, tier }: StageSwatchDotProps) {
  const scope = useHatchScope();
  return (
    <span
      aria-hidden="true"
      className={`relative inline-block h-2.5 w-2.5 shrink-0 overflow-hidden rounded-xs ${bgClass}`}
    >
      {tier > 0 ? (
        <svg viewBox="0 0 10 10" className="absolute inset-0 h-full w-full" role="presentation">
          <StageHatchDefs scope={scope} scale={0.5} />
          <rect x={0} y={0} width={10} height={10} fill={hatchFill(scope, tier)} />
        </svg>
      ) : null}
    </span>
  );
}
